import { useCallback, useEffect, useRef } from 'react';

const RECONNECT_MS = 5000;

export default function useSessionPresenceWebSocket(enabled = true, label = 'Session', onMessage = null, subscribe = null) {
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const subscribeRef = useRef(subscribe);
  subscribeRef.current = subscribe;

  const connect = useCallback(() => {
    if (!enabled) return;
    if (wsRef.current && wsRef.current.readyState <= 1) return;

    // SECURITY: no JWT in the URL anymore. The browser attaches the nf_token
    // httpOnly cookie automatically on the upgrade request; the server reads
    // it and authenticates the socket. Falls back to `session:` if the cookie
    // isn't present (legacy Passport session path).
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log(`${label} WebSocket connected`);
      ws.send('session:');
      if (subscribeRef.current && subscribeRef.current.roomKey) {
        ws.send(JSON.stringify({ type: 'subscribe_room', roomKey: subscribeRef.current.roomKey, display_name: subscribeRef.current.display_name || 'Guest' }));
      }
    };

    ws.onmessage = (event) => {
      if (onMessageRef.current) onMessageRef.current(event);
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (enabled) reconnectTimerRef.current = setTimeout(connect, RECONNECT_MS);
    };

    ws.onerror = () => {
      try { ws.close(); } catch (e) {}
    };

    wsRef.current = ws;
  }, [enabled, label]);

  useEffect(() => {
    if (!enabled) return undefined;
    connect();

    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) {}
      }
    };
  }, [enabled, connect]);

  return wsRef;
}
